import { TriangleAlert } from "lucide-react";

import { ModalWrapper } from "@/components/modals/modal-wrapper";
import { popModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";

/** 배경·확대 인셋·하이라이트가 합성된 시안 이미지 그대로 사용한다. */
const STEP_2_IMAGE = "/guide/step-2.png";
const STEP_3_IMAGE = "/guide/step-3.png";

const StepNumber = ({ children }: { children: string }) => (
  <span className="caption-2-medium flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-color text-static-white">
    {children}
  </span>
);

const StepImage = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} className="w-full rounded-lg" />
);

/** guideOnly: 안내 배너에서 직접 연 경우. 업로드 실패로 뜬 경우에는 상단 경고를 함께 보여준다. */
export const UploadFailedModal = ({
  guideOnly = false,
}: {
  guideOnly?: boolean;
}) => {
  return (
    <ModalWrapper className="flex max-h-[85vh] w-[600px] max-w-[92vw] flex-col gap-0 rounded-2xl border-none bg-background-base p-0 shadow-lg">
      <h2 className="headline-2-semibold shrink-0 px-6 pb-0 pt-5 text-label-normal">
        제출용 IXD 파일을 편집용으로 변환하기
      </h2>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
        {!guideOnly && (
          <div className="flex gap-2 rounded-lg bg-red-50 px-4 py-3">
            <TriangleAlert className="mt-px size-[18px] shrink-0 text-status-negative" />
            <div className="flex flex-col gap-1">
              <p className="label-2-medium text-status-negative break-keep">
                XBRL 편집기의 &lsquo;제출용 파일 생성&rsquo;으로 생성한 IXD
                파일은 인벡터에 업로드할 수 없습니다.
              </p>
              <p className="caption-1-regular text-label-alternative break-keep">
                아래 방법으로 편집용 파일로 변환한 뒤 다시 업로드해주세요.
              </p>
            </div>
          </div>
        )}

        <ol className="flex flex-col gap-6">
          <li className="flex items-start gap-2">
            <StepNumber>1</StepNumber>
            <p className="label-2-medium leading-5 text-label-normal break-keep">
              업로드되지 않는 &lsquo;제출용 파일&rsquo;을 금융감독원 XBRL
              편집기로 엽니다.
            </p>
          </li>

          <li className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <StepNumber>2</StepNumber>
              <p className="label-2-medium leading-5 text-label-normal break-keep">
                잠시 후 아래 모달이 뜨면 &lsquo;아니오&rsquo;를 클릭합니다.
              </p>
            </div>
            <StepImage
              src={STEP_2_IMAGE}
              alt="금융감독원 XBRL 편집기의 변환 여부 확인 모달 — '아니오' 클릭"
            />
            <p className="caption-1-regular text-label-alternative break-keep">
              * 최신 버전 택소노미로 변환 파일인가요?
              <br />위 모달 대신 &lsquo;최신 버전으로 변환할까요?&rsquo;를 묻는
              모달이 나타날 수 있어요. 원하는 항목을 선택해 넘어가면 됩니다.
            </p>
          </li>

          <li className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
              <StepNumber>3</StepNumber>
              <p className="label-2-medium leading-5 text-label-normal break-keep">
                편집기 상단 &lsquo;다른 이름으로 저장&rsquo;을 눌러 원하는
                이름으로 저장합니다.
              </p>
            </div>
            <StepImage
              src={STEP_3_IMAGE}
              alt="XBRL 편집기 상단 툴바의 '다른 이름으로 저장' 버튼 위치"
            />
          </li>

          <li className="flex items-start gap-2">
            <StepNumber>4</StepNumber>
            <p className="label-2-medium leading-5 text-label-normal break-keep">
              저장된 파일은 편집용으로 변환되어 인벡터에 업로드할 수 있습니다.
            </p>
          </li>
        </ol>
      </div>

      <div className="flex shrink-0 justify-end gap-2 px-6 pb-5">
        {guideOnly ? (
          <Button variant="solid" color="primary" size={36} onClick={popModal}>
            닫기
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              color="secondary"
              size={36}
              onClick={popModal}
            >
              닫기
            </Button>
            <Button
              variant="solid"
              color="primary"
              size={36}
              onClick={popModal}
            >
              다시 업로드하기
            </Button>
          </>
        )}
      </div>
    </ModalWrapper>
  );
};
